import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography, Box, Button, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../components/Layout';
import forumService from '../services/forumService';
import { useUnion } from '../contexts/UnionContext';

function Forum() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeUnion } = useUnion();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // New topic dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);

  const loadTopics = useCallback(async () => {
    if (!activeUnion) return;
    setLoading(true);
    setError('');
    try {
      const data = await forumService.getTopics();
      setTopics(Array.isArray(data) ? data : data.results || []);
    } catch {
      setError(t('forum.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [activeUnion, t]);

  useEffect(() => { loadTopics(); }, [loadTopics]);

  const handleCreate = async () => {
    if (!newTitle.trim() || !newBody.trim()) return;
    setCreating(true);
    try {
      const topic = await forumService.createTopic({
        title: newTitle.trim(),
        body: newBody.trim(),
      });
      setDialogOpen(false);
      setNewTitle('');
      setNewBody('');
      navigate(`/forum/${topic.id}`);
    } catch {
      setError(t('forum.failedCreate'));
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('et-EE', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('forum.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          {t('forum.newTopic')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : topics.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('forum.noTopics')}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('forum.topic')}</TableCell>
                <TableCell>{t('forum.author')}</TableCell>
                <TableCell align="center">{t('forum.replies')}</TableCell>
                <TableCell>{t('forum.lastActivity')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topics.map((tp) => (
                <TableRow
                  key={tp.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/forum/${tp.id}`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tp.is_pinned && <PushPinIcon fontSize="small" color="primary" />}
                      <Typography variant="body1" sx={{ fontWeight: tp.is_pinned ? 600 : 400 }}>
                        {tp.title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {tp.author_name || tp.author_username}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={tp.reply_count ?? 0} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(tp.last_reply_at || tp.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New Topic Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('forum.newTopic')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label={t('forum.titleLabel')} value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth label={t('forum.bodyLabel')} value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            multiline rows={5}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained" onClick={handleCreate}
            disabled={creating || !newTitle.trim() || !newBody.trim()}
          >
            {creating ? <CircularProgress size={20} /> : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default Forum;
